/**
 * Type-menu op de plek van de Apple Pencil (lang drukken met de pen op een
 * lege plek van het canvas). Zelfde typen als het typepaneel voor dit
 * diagramtype, met een zoekveld; kiezen zet het object op die plek.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRegistry, resolveBilingual } from "../../registry";
import { colorForConcept, colorForType } from "../layerColors";
import { LAYER_LABELS, usePaletteData } from "../palette/usePaletteData";
import { panelSwatch } from "../panelSwatch";
import { getElementIcon, getStereotypeIcon } from "./elementIcons";

/** Eén keuze in het menu: een type, eventueel met stereotypes of beginwaarden. */
export interface PenKeuze {
  /** Unieke sleutel (ook voor de lijst "laatst gebruikt"). */
  key: string;
  typeId: string;
  label: Record<string, string>;
  kleuren: { bg: string; border: string; text: string };
  icoon: React.ReactNode;
  defaultStereotypes?: string[];
  defaultAttributes?: Record<string, unknown>;
  folderClass: string;
}

const RECENT_KEY = "ae.penMenu.recent";

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((s): s is string => typeof s === "string") : [];
  } catch {
    return [];
  }
}

function writeRecent(key: string): void {
  try {
    const next = [key, ...readRecent().filter((id) => id !== key)].slice(0, 6);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    // alleen een gemak; zonder opslag werkt het menu gewoon
  }
}

export interface PenMenuStand {
  /** Positie in het canvas-element (voor het menu). */
  x: number;
  y: number;
  /** Positie op het scherm (voor het neerzetten). */
  clientX: number;
  clientY: number;
}

export function PenTypeMenu({
  stand,
  viewKind,
  onPick,
  onClose,
}: {
  stand: PenMenuStand;
  viewKind: string | null;
  onPick: (keuze: PenKeuze) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const ref = useRef<HTMLDivElement | null>(null);
  const [zoek, setZoek] = useState("");
  const veeg = useRef<{ x: number; y: number } | null>(null);
  // Pointer-up en click kunnen beide komen; één keuze per menu.
  const gekozen = useRef(false);
  const kies = (k: PenKeuze) => {
    if (gekozen.current) return;
    gekozen.current = true;
    writeRecent(k.key);
    onPick(k);
  };

  useEffect(() => {
    const opDruk = (e: PointerEvent) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    const opToets = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("pointerdown", opDruk, true);
    window.addEventListener("keydown", opToets);
    return () => {
      window.removeEventListener("pointerdown", opDruk, true);
      window.removeEventListener("keydown", opToets);
    };
  }, [onClose]);

  const { grouped, paletteGroups } = usePaletteData({ activeViewKind: viewKind });

  // Alle keuzes uit het typepalet voor dit diagramtype, in dezelfde groepen en
  // volgorde als het typepaneel (laag-groepen, dan C4 / Sharp / BMC / tekenen).
  const secties = useMemo(() => {
    const reg = getRegistry();
    const uit: Array<{ id: string; titel: string; items: PenKeuze[] }> = [];
    const groepenNaLaag = new Map<string, typeof paletteGroups>();
    const losseGroepen: typeof paletteGroups = [];
    const zichtbareLagen = new Set(grouped.map((g) => g.layer));
    for (const g of paletteGroups) {
      if (g.after_layer && zichtbareLagen.has(g.after_layer)) {
        const arr = groepenNaLaag.get(g.after_layer) ?? [];
        arr.push(g);
        groepenNaLaag.set(g.after_layer, arr);
      } else losseGroepen.push(g);
    }
    const groepSectie = (g: (typeof paletteGroups)[number]) => {
      const items: PenKeuze[] = [];
      for (const [idx, entry] of g.entries.entries()) {
        if (entry.kind === "relation") continue; // een lijn heeft bron en doel nodig
        const target = reg.getElementType(entry.type_id);
        const labelBron = entry.label_override ?? target?.label;
        if (!labelBron) continue;
        const stereo = entry.default_stereotypes ?? [];
        const layer = target?.layer ?? "composite";
        items.push({
          key: `g:${g.id}:${entry.type_id}:${stereo.join(",")}:${idx}`,
          typeId: entry.type_id,
          label: labelBron,
          kleuren:
            stereo.length > 0
              ? colorForConcept({ typeId: entry.type_id, layer, stereotypes: stereo })
              : colorForType(entry.type_id, layer),
          icoon: getStereotypeIcon(stereo) ?? getElementIcon(entry.type_id),
          ...(stereo.length > 0 ? { defaultStereotypes: stereo } : {}),
          ...(entry.default_attributes && Object.keys(entry.default_attributes).length > 0
            ? { defaultAttributes: entry.default_attributes }
            : {}),
          folderClass: g.folder_class ?? "archimate",
        });
      }
      return { id: `group:${g.id}`, titel: resolveBilingual(g.label, lang), items };
    };
    for (const { layer, types } of grouped) {
      const items: PenKeuze[] = types.map((row) => {
        const type = row.elementType;
        return {
          key: `l:${layer}:${row.dragTypeId ?? type.id}:${(row.defaultStereotypes ?? []).join(",")}`,
          typeId: row.dragTypeId ?? type.id,
          label: type.label,
          kleuren: colorForType(type.id, type.layer),
          icoon: getElementIcon(type.id),
          ...(row.defaultStereotypes && row.defaultStereotypes.length > 0
            ? { defaultStereotypes: row.defaultStereotypes }
            : {}),
          folderClass: "archimate",
        };
      });
      uit.push({
        id: `layer:${layer}`,
        titel: LAYER_LABELS[layer] ? t(LAYER_LABELS[layer]) : layer,
        items,
      });
      for (const g of groepenNaLaag.get(layer) ?? []) uit.push(groepSectie(g));
    }
    for (const g of losseGroepen) uit.push(groepSectie(g));
    return uit.filter((sec) => sec.items.length > 0);
  }, [grouped, paletteGroups, lang, t]);

  const lijst = useMemo(() => {
    const alle = secties.flatMap((sec) => sec.items);
    const q = zoek.trim().toLowerCase();
    if (q) {
      return {
        recent: [] as PenKeuze[],
        secties: [
          {
            id: "zoek",
            titel: "",
            items: alle.filter((k) => resolveBilingual(k.label, lang).toLowerCase().includes(q)),
          },
        ],
      };
    }
    const recent = readRecent()
      .map((key) => alle.find((k) => k.key === key))
      .filter((k): k is PenKeuze => k !== undefined);
    return { recent, secties };
  }, [secties, zoek, lang]);
  const aantal = lijst.recent.length + lijst.secties.reduce((n, sec) => n + sec.items.length, 0);

  // Binnen het scherm houden: het menu is ~240 × 340 px.
  const breedte = 240;
  const hoogte = 340;
  const maxX = (ref.current?.offsetParent?.clientWidth ?? window.innerWidth) - breedte - 8;
  const maxY = (ref.current?.offsetParent?.clientHeight ?? window.innerHeight) - hoogte - 8;
  const left = Math.max(8, Math.min(stand.x, maxX));
  const top = Math.max(8, Math.min(stand.y, maxY));

  return (
    <div
      ref={ref}
      role="menu"
      onPointerDown={(e) => e.stopPropagation()}
      aria-label={t("canvas.penMenu.title")}
      className="absolute z-50 flex flex-col gap-1 rounded-lg border border-(--ld-border) bg-(--ld-surface) p-2 shadow-lg"
      style={{
        left,
        top,
        width: breedte,
        maxHeight: hoogte,
        touchAction: "pan-y",
        WebkitUserSelect: "none",
        WebkitTouchCallout: "none",
      }}
    >
      {/* Pen-bediening: een tik laat bij Scribble soms een losse punt achter
          (wordt gefilterd); veeg naar links = laatste teken weg (ver = laatste
          woord); ⌫ en ✕ zijn grote knoppen voor als vegen niet lukt. */}
      <div
        className="flex items-stretch gap-1"
        onPointerDown={(e) => {
          if (e.pointerType === "pen") veeg.current = { x: e.clientX, y: e.clientY };
        }}
        onPointerUp={(e) => {
          const b = veeg.current;
          veeg.current = null;
          if (!b || e.pointerType !== "pen") return;
          const dx = e.clientX - b.x;
          if (dx < -40 && Math.abs(e.clientY - b.y) < 20) {
            setZoek((z) => (dx < -140 ? z.replace(/\s*\S+\s*$/, "") : z.slice(0, -1)));
          }
        }}
      >
        <input
          type="search"
          value={zoek}
          onChange={(e) => setZoek(e.target.value.replace(/[.·•]/g, ""))}
          placeholder={t("canvas.penMenu.search")}
          style={{ WebkitUserSelect: "text" }}
          className="min-w-0 flex-1 rounded border border-(--ld-border) bg-(--ld-bg) px-2 py-1 text-(--ld-text) text-sm"
        />
      </div>
      <div className="flex flex-col gap-1 overflow-y-auto">
        {lijst.recent.length > 0 && (
          <Sectie titel={t("canvas.penMenu.recent")}>
            {lijst.recent.map((k) => (
              <Knop key={`r:${k.key}`} keuze={k} lang={lang} kies={kies} />
            ))}
          </Sectie>
        )}
        {lijst.secties.map((sec) => (
          <Sectie key={sec.id} titel={sec.titel}>
            {sec.items.map((k) => (
              <Knop key={k.key} keuze={k} lang={lang} kies={kies} />
            ))}
          </Sectie>
        ))}
        {aantal === 0 && (
          <p className="px-2 py-1 text-(--ld-muted) text-sm">{t("canvas.penMenu.none")}</p>
        )}
      </div>
    </div>
  );
}

function Sectie({ titel, children }: { titel: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-1">
      {titel && (
        <h3 className="sticky top-0 bg-(--ld-surface) px-1 pt-1 font-medium text-(--ld-muted) text-xs">
          {titel}
        </h3>
      )}
      <ul className="flex flex-col gap-1">{children}</ul>
    </section>
  );
}

function Knop({
  keuze,
  lang,
  kies,
}: {
  keuze: PenKeuze;
  lang: string;
  kies: (k: PenKeuze) => void;
}) {
  const swatch = panelSwatch(keuze.kleuren);
  return (
    <li>
      <button
        type="button"
        role="menuitem"
        onPointerUp={(e) => {
          if (e.pointerType === "mouse") return; // muis: onClick
          e.stopPropagation();
          kies(keuze);
        }}
        onClick={() => kies(keuze)}
        style={swatch.style}
        className={`flex min-h-11 w-full items-center gap-2 rounded border px-2 text-left text-sm ${swatch.className}`}
      >
        {keuze.icoon ? (
          <span aria-hidden className="block h-4 w-4 shrink-0 opacity-80">
            {keuze.icoon}
          </span>
        ) : (
          <span
            aria-hidden
            className="block h-2 w-2 shrink-0 rounded-full"
            style={{ backgroundColor: keuze.kleuren.border }}
          />
        )}
        <span className="truncate">{resolveBilingual(keuze.label, lang)}</span>
      </button>
    </li>
  );
}
